import { PushNotifications } from "@capacitor/push-notifications";
import { IonToast, isPlatform } from "@ionic/react";
import { ChatContextEffects, ChatProvider } from "@src/app/chat/chatProvider";
import { SENT_HOME_REQUEST_SUCCESS_MESSAGE_OPTION } from "@src/app/hcpShifts/constants";
import { useAppSelector } from "@src/app/store";
import { useSession } from "@src/app/store/helperHooks";
import { setShowToast } from "@src/app/store/shift";
import { useZendeskMessaging } from "@src/app/zendesk/useZendeskMessaging";
import { AppV2PrivateRoutes } from "@src/appV2/App";
import { CbhFeatureFlag, FEATURE_FLAG_DEFAULT_VALUES, useCbhFlag } from "@src/appV2/FeatureFlags";
import { ActionType, LocalStorage } from "@store/session";
import * as AppboyPlugin from "appboy-cordova-sdk/www/AppboyPlugin";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Redirect, Route } from "react-router-dom";

import { isAgentInSignupProcess } from "./isAgentInSignupProcess";
import { OnBoardingRoutes } from "../../onboarding";
import { OnboardToStripe } from "../../onboardingStripe";
import { SignupBonusClaim } from "../../signupBonusClaim";
import { AppTabs } from "../../tabs";
import { GlobalRouterPath } from "../constant/globalRoute";
import { TabRouterPath } from "../constant/tabRoute";

export function HomeRoutes() {
  const session = useSession();
  const { agent, userId, isSignup } = session;
  const dispatch = useDispatch();

  useZendeskMessaging();

  const wasPromptedToLogOutForICAgreement = JSON.parse(
    localStorage.getItem(LocalStorage.WAS_LOGGED_OUT_FOR_NEW_IC_AGREEMENT) ?? "false"
  );
  const shouldSignLatestAgreement =
    !isAgentInSignupProcess(agent ?? {}) && agent?.shouldSignLatestAgreement;

  useEffect(() => {
    if (shouldSignLatestAgreement && !wasPromptedToLogOutForICAgreement) {
      dispatch({
        type: ActionType.SHOW_IC_AGREEMENT_POPUP,
      });
      return;
    }

    dispatch({ type: ActionType.HIDE_IC_AGREEMENT_POPUP });
  }, [dispatch, shouldSignLatestAgreement, wasPromptedToLogOutForICAgreement]);

  useEffect(() => {
    if (!isPlatform("capacitor") || !userId) {
      return;
    }
    // identifying the user on Braze
    AppboyPlugin.changeUser(userId);
    PushNotifications.requestPermissions().then((result) => {
      setTimeout(() => {
        if (result.receive === "granted") {
          PushNotifications.register();
        }
      }, 100);
    });
  }, [userId]);

  const showShiftCancelSuccessToast = useAppSelector(
    (store) => store.shiftStore.showToast === SENT_HOME_REQUEST_SUCCESS_MESSAGE_OPTION.toastName
  );

  const { successToast: sentHomeSuccessToastOptions } = useCbhFlag(
    CbhFeatureFlag.SENT_HOME_REQUEST_CONFIG,
    { defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.SENT_HOME_REQUEST_CONFIG] }
  );

  if (!agent || !agent.userId || !userId) {
    if (isSignup) {
      return <OnBoardingRoutes agent={agent} />;
    } else {
      return <Redirect to={{ pathname: GlobalRouterPath.NO_USER_PAGE }} />;
    }
  }

  if (isAgentInSignupProcess(agent)) {
    return <OnBoardingRoutes agent={agent} />;
  }

  if (
    agent.canBeOnboardedInStripe &&
    (!agent.paymentAccountInfo || (shouldSignLatestAgreement && wasPromptedToLogOutForICAgreement))
  ) {
    return (
      <OnboardToStripe
        nextStagePath={TabRouterPath.PAYMENT_SERVICE_ONBOARDING}
        disableBackButton={true}
      />
    );
  }
  if (
    !agent.hasClaimedBonus &&
    agent.applicantId &&
    agent.signupExperimentId &&
    agent.signupConfig &&
    agent.signupConfig.signingBonusAmount1 &&
    agent.signupConfig.signingBonusAmount2
  ) {
    return <SignupBonusClaim />;
  }

  return (
    <ChatProvider userId={userId} agent={agent}>
      <ChatContextEffects>
        <Route path={GlobalRouterPath.HOME}>
          <AppTabs />
        </Route>
        <Route path={GlobalRouterPath.APP_V2_HOME}>
          <AppV2PrivateRoutes />
        </Route>
      </ChatContextEffects>
      <IonToast
        isOpen={showShiftCancelSuccessToast}
        header={
          sentHomeSuccessToastOptions?.header ?? SENT_HOME_REQUEST_SUCCESS_MESSAGE_OPTION.header
        }
        onDidDismiss={() => dispatch(setShowToast())}
        message={
          sentHomeSuccessToastOptions?.message ?? SENT_HOME_REQUEST_SUCCESS_MESSAGE_OPTION.message
        }
        duration={SENT_HOME_REQUEST_SUCCESS_MESSAGE_OPTION.duration}
        color="success"
        position="top"
        data-testid="fcf-success-message"
      />
    </ChatProvider>
  );
}
